import { type Dispatch, type FC, type SetStateAction, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/material';
import {
  type Control,
  useForm,
  type UseFormTrigger,
  type UseFormWatch,
} from 'react-hook-form';

import { useIsMobile } from '@/hooks/useBreakpoints';
import {
  type MarketMakingFormValues,
  type CampaignFormValues,
  CampaignType,
  type ThresholdFormValues,
  type HoldingFormValues,
  type CompetitiveMmFormValues,
  type ThresholdMmFormValues,
} from '@/types';
import { scrollToFirstErrorFieldOnMobile } from '@/utils';

import { campaignValidationSchema } from '../validation';

import {
  BottomNavigation,
  CompetitiveMmForm,
  HoldingForm,
  MarketMakingForm,
  ThresholdForm,
  ThresholdMmForm,
} from '.';

type Props = {
  formValues: CampaignFormValues;
  setFormValues: (values: CampaignFormValues) => void;
  handleChangeStep: Dispatch<SetStateAction<number>>;
};

const EscrowDetailsStep: FC<Props> = ({
  formValues,
  setFormValues,
  handleChangeStep,
}) => {
  const formId = 'escrow-details-form';
  const isMobile = useIsMobile();

  const {
    control,
    formState: { errors },
    watch,
    trigger,
    handleSubmit,
  } = useForm<CampaignFormValues>({
    mode: isMobile ? 'onSubmit' : 'onBlur',
    resolver: yupResolver(campaignValidationSchema),
    defaultValues: formValues,
    shouldFocusError: isMobile,
  });

  const campaignType = formValues.type as CampaignType;

  useEffect(() => {
    scrollToFirstErrorFieldOnMobile(isMobile, errors);
  }, [isMobile, errors]);

  const handleBackClick = () => {
    handleChangeStep((prev) => prev - 1);
  };

  const onSubmit = async (data: CampaignFormValues) => {
    setFormValues(data);
    handleChangeStep((prev) => prev + 1);
  };

  return (
    <>
      <Stack
        sx={{
          mt: 4,
          width: '100%',
          gridArea: 'main',
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Stack
          component="form"
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          direction="row"
          sx={{
            justifyContent: 'space-between',
            gap: { sm: 3, md: 2 },
            overflowY: { md: 'auto' },
            pr: { md: 2 },
            scrollbarGutter: { md: 'stable' },
          }}
        >
          <Stack
            sx={{
              gap: { xs: 4, md: 3 },
              width: { xs: '100%', md: '600px' },
              '& .MuiFormHelperText-root': {
                mt: 0.5,
                mx: 0,
                lineHeight: 1,
              },
            }}
          >
            {campaignType === CampaignType.MARKET_MAKING && (
              <MarketMakingForm
                control={control as Control<MarketMakingFormValues>}
                errors={errors}
                watch={watch as UseFormWatch<MarketMakingFormValues>}
                trigger={trigger as UseFormTrigger<MarketMakingFormValues>}
                campaignType={campaignType}
              />
            )}
            {campaignType === CampaignType.HOLDING && (
              <HoldingForm
                control={control as Control<HoldingFormValues>}
                errors={errors}
                watch={watch as UseFormWatch<HoldingFormValues>}
                trigger={trigger as UseFormTrigger<HoldingFormValues>}
                campaignType={campaignType}
              />
            )}
            {campaignType === CampaignType.THRESHOLD && (
              <ThresholdForm
                control={control as Control<ThresholdFormValues>}
                errors={errors}
                watch={watch as UseFormWatch<ThresholdFormValues>}
                trigger={trigger as UseFormTrigger<ThresholdFormValues>}
                campaignType={campaignType}
              />
            )}
            {campaignType === CampaignType.COMPETITIVE_MARKET_MAKING && (
              <CompetitiveMmForm
                control={control as Control<CompetitiveMmFormValues>}
                errors={errors}
                watch={watch as UseFormWatch<CompetitiveMmFormValues>}
                trigger={trigger as UseFormTrigger<CompetitiveMmFormValues>}
                campaignType={campaignType}
              />
            )}
            {campaignType === CampaignType.THRESHOLD_MARKET_MAKING && (
              <ThresholdMmForm
                control={control as Control<ThresholdMmFormValues>}
                errors={errors}
                watch={watch as UseFormWatch<ThresholdMmFormValues>}
                trigger={trigger as UseFormTrigger<ThresholdMmFormValues>}
                campaignType={campaignType}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
      <BottomNavigation
        formId={formId}
        handleNextClick={() => {}}
        handleBackClick={handleBackClick}
      />
    </>
  );
};

export default EscrowDetailsStep;
